// 던전 클리어 연출: 화면 번쩍(한 번) → 광선 → 「CLEAR」 로고 쾅 → 별 3개가 하나씩 쾅쾅 → 결과 표로 넘어간다.
// 보스 결정타 슬로·정지는 게임(KO.boss…), 엔딩 곡·징글은 오디오(audio/bgm.ts)가 맡고 여기서는 화면과 효과음 신호(dh-ui-sfx)만 낸다.
// 애니메이션은 WAAPI 한 번씩(프레임 루프 JS 없음), 파티클은 SparkField(상한 있는 풀). 눌러서 건너뛴다(skip).
import { assetUrl } from '../pwa/assetMap';
import { el } from './dom';
import { installClearStyle } from './clearStyle';
import { SparkField } from './sparkField';
import { TIMELINE, particleBudget } from './rewardCardsModel';

/** 효과음 신호: 오디오 엔진이 이름을 받아 기존 효과음으로 낸다(audio/index.ts) */
export function uiSfx(name: string): void {
  try { window.dispatchEvent(new CustomEvent('dh-ui-sfx', { detail: name })); } catch { /* 소리 없이 진행 */ }
}

export interface ClearOptions {
  /** 별 사유 비트(1 클리어·2 5분 이내·4 HP 50%). 켜진 별은 금색, 꺼진 별은 어둡게 */
  starMask: number;
  reduceFlash: boolean;
  reducedMotion: boolean;
}

export class ClearFx {
  readonly root: HTMLDivElement;
  private readonly flash: HTMLDivElement;
  private readonly rays: HTMLImageElement;
  private readonly ring: HTMLDivElement;
  private readonly logo: HTMLDivElement;
  private readonly stars: HTMLDivElement[] = [];
  private readonly hint: HTMLDivElement;
  private readonly sparks: SparkField;
  private anims: Animation[] = [];
  private timers: number[] = [];
  private done: (() => void) | null = null;
  private startedAt = 0;
  /** QA: 지금 연출 중인지, 지금까지 연출한 횟수 */
  active = false;
  played = 0;

  constructor(parent: HTMLElement, reduced = false) {
    installClearStyle();
    const small = Math.min(parent.clientWidth || 1280, parent.clientHeight || 720) <= 480;
    const root = el('div', '', parent);
    root.className = 'dh-clear'; root.dataset.testid = 'clear-fx'; root.setAttribute('aria-hidden', 'true');
    this.root = root;
    this.rays = el('img', '', root); this.rays.className = 'dh-clear-rays' + (small ? ' small' : ''); this.rays.alt = ''; this.rays.draggable = false;
    this.rays.src = assetUrl('ui/cards/rays-gold.webp');
    this.rays.addEventListener('error', () => { this.rays.style.display = 'none'; });
    this.ring = el('div', '', root); this.ring.className = 'dh-clear-ring';
    this.logo = el('div', '', root); this.logo.className = 'dh-clear-logo';
    const li = el('img', '', this.logo); li.alt = 'CLEAR'; li.draggable = false; li.src = assetUrl('ui/cards/clear-logo.webp');
    li.addEventListener('error', () => this.logo.classList.add('noimg'));
    el('div', '', this.logo, 'CLEAR').className = 'txt';
    const row = el('div', '', root); row.className = 'dh-clear-stars';
    for (let i = 0; i < 3; i++) {
      const s = el('div', '', row); s.className = 'dh-clear-star';
      const on = el('img', '', s); on.alt = ''; on.draggable = false;
      on.addEventListener('error', () => s.classList.add('noimg'));
      el('div', '', s, '★').className = 'fb';
      this.stars.push(s);
    }
    this.sparks = new SparkField(root, particleBudget(parent.clientWidth || 1280, parent.clientHeight || 720, reduced));
    this.flash = el('div', '', root); this.flash.className = 'dh-clear-flash';
    this.hint = el('div', '', root, '화면을 누르면 넘어갑니다'); this.hint.className = 'dh-clear-hint';
    root.addEventListener('click', () => { if (this.active && performance.now() - this.startedAt > 350) this.finish(); });
  }

  /** 연출을 시작한다. 끝나거나 건너뛰면 onDone이 한 번 불린다 */
  play(o: ClearOptions, onDone: () => void): void {
    this.cancel();
    this.done = onDone; this.active = true; this.played++; this.startedAt = performance.now();
    const root = this.root;
    root.style.display = 'flex'; root.style.opacity = '1';
    const w = root.clientWidth || 1280, h = root.clientHeight || 720;
    this.sparks.resize(w, h);
    const cx = w / 2, cy = h * 0.44;
    const T = TIMELINE;
    // 별 그림(켜짐/꺼짐)
    for (let i = 0; i < 3; i++) {
      const on = (o.starMask & (1 << i)) !== 0;
      const s = this.stars[i];
      s.classList.toggle('off', !on);
      (s.firstElementChild as HTMLImageElement).src = assetUrl(on ? 'ui/cards/star-gold.webp' : 'ui/cards/star-empty.webp');
      s.style.opacity = '0';
    }
    this.logo.style.opacity = '0'; this.ring.style.opacity = '0'; this.rays.style.opacity = '0'; this.flash.style.opacity = '0'; this.hint.style.opacity = '0';
    if (o.reducedMotion) { this.settle(); this.later(900, () => this.finish()); return; }

    // 1) 번쩍: 한 번만(섬광 1초 3회 이하). 섬광 약화 설정이면 약하게
    const flashPeak = o.reduceFlash ? 0.28 : 0.8;
    this.run(this.flash, [{ opacity: 0 }, { opacity: flashPeak, offset: 0.2 }, { opacity: 0 }], T.flashMs * 1.6, 'linear');
    // 2) 광선이 번지며 돈다
    this.run(this.rays, [{ opacity: 0, scale: '0.4' }, { opacity: 0.85, scale: '1' }], 700, 'ease-out', T.logoAtMs, 'forwards');
    // 3) CLEAR 로고: 크게 → 쾅 → 살짝 되돌림
    uiSfx('clearLogo'); // 보스 쓰러짐·승리 징글은 게임 이벤트가 이미 냈다. 여기는 로고가 쾅 떨어지는 소리
    this.run(this.logo, [
      { opacity: 0, transform: 'scale(3.4) rotate(-4deg)' },
      { opacity: 1, transform: 'scale(.92) rotate(0)', offset: 0.55 },
      { opacity: 1, transform: 'scale(1.08)', offset: 0.78 },
      { opacity: 1, transform: 'scale(1)' },
    ], T.logoMs, 'cubic-bezier(.2,.9,.25,1)', T.logoAtMs, 'forwards');
    this.run(this.ring, [{ opacity: 0.95, transform: 'scale(.2)' }, { opacity: 0, transform: 'scale(2.8)' }], 640, 'ease-out', T.logoAtMs + T.logoMs * 0.5, 'backwards');
    this.later(T.logoAtMs + T.logoMs * 0.55, () => this.sparks.burst(cx, cy, 30, Math.min(w, h) * 0.9));
    // 4) 별이 하나씩 쾅
    for (let i = 0; i < 3; i++) {
      const at = T.starFirstMs + i * T.starGapMs;
      const on = (o.starMask & (1 << i)) !== 0;
      this.run(this.stars[i], [
        { opacity: 0, transform: 'translateY(-70px) scale(2.6) rotate(-25deg)' },
        { opacity: 1, transform: 'translateY(0) scale(.88) rotate(4deg)', offset: 0.62 },
        { opacity: 1, transform: 'scale(1.1) rotate(0)', offset: 0.82 },
        { opacity: 1, transform: 'scale(1)' },
      ], T.starMs, 'cubic-bezier(.3,.8,.3,1)', at, 'forwards');
      this.later(at + T.starMs * 0.6, () => {
        uiSfx(on ? 'hitFinisher' : 'uiClick');
        const r = this.stars[i].getBoundingClientRect(), rr = root.getBoundingClientRect();
        if (on) this.sparks.burst(r.left - rr.left + r.width / 2, r.top - rr.top + r.height / 2, 14, Math.min(w, h) * 0.55);
      });
    }
    this.later(T.starFirstMs + 3 * T.starGapMs, () => { this.hint.style.opacity = '1'; });
    this.later(T.clearEndMs, () => this.finish());
  }

  /** 건너뛰기·끝: 그림을 보여 준 채 부드럽게 사라지고 onDone */
  finish(): void {
    if (!this.active) return;
    this.active = false;
    for (const t of this.timers) clearTimeout(t);
    this.timers.length = 0;
    const cb = this.done; this.done = null;
    const a = this.root.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 320, easing: 'ease-in', fill: 'forwards' });
    // 페이드도 anims에 넣는다: forwards로 남은 불투명도 0이 다음 play()의 연출을 가리지 않게 cancel()이 함께 지운다
    this.anims.push(a);
    a.onfinish = () => { if (!this.active) { this.root.style.display = 'none'; this.cancelAnims(); this.sparks.clear(); } };
    if (cb) cb();
  }

  /** 장면이 바뀌었다: 아무 것도 남기지 않고 끈다(onDone 없음) */
  cancel(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers.length = 0;
    this.cancelAnims();
    this.active = false; this.done = null;
    this.root.style.display = 'none';
    this.sparks.clear();
  }

  /** QA: 동시에 살아 있던 파티클 최댓값·낸 수·상한 */
  stats(): { peak: number; spawned: number; capacity: number } { return { peak: this.sparks.peak, spawned: this.sparks.spawned, capacity: this.sparks.capacity }; }

  private settle(): void {
    this.logo.style.opacity = '1'; this.rays.style.opacity = '0.6';
    for (const s of this.stars) s.style.opacity = '1';
  }
  private run(target: HTMLElement, keyframes: Keyframe[], duration: number, easing: string, delay = 0, fill: FillMode = 'none'): void {
    this.anims.push(target.animate(keyframes, { duration, easing, delay, fill }));
  }
  private later(ms: number, fn: () => void): void {
    this.timers.push(window.setTimeout(() => { if (this.active) fn(); }, ms));
  }
  private cancelAnims(): void {
    for (const a of this.anims) { try { a.cancel(); } catch { /* 이미 끝남 */ } }
    this.anims.length = 0;
  }
}
